/**
 * Derived Publisher dashboard data, plus the mock-snapshot materialiser the dashboard reads.
 *
 * The dashboard is still release-centric — `PublisherRelease` is the record the product owns —
 * but a useful home screen asks a few questions the raw list does not answer directly:
 * how many songs are already published, which releases need attention, what delivery facts the
 * provider has actually reported, and what has happened most recently across the whole area.
 *
 * Pure functions only. No store access, no routing and no rendering, so the route, the store and
 * the tests can all reuse one set of derivations.
 */
import { describeQueueStatus } from '../contracts/labelgrid';
import { NO_DATE, languageLabel } from './publisherFormat';
import type {
  PublisherRelease,
  PublisherReleaseEvent,
  PublisherReleaseEventType,
  PublisherReleasePlatform,
  PublisherStateSnapshot,
  Song,
} from '../types/domain';

/** A small counted bucket, for the compact metadata summaries. */
export interface PublisherDashboardBucket {
  label: string;
  count: number;
}

export type PublisherDashboardActivityFilter =
  | 'all'
  | 'milestones'
  | 'platform_updates'
  | 'earnings'
  | 'issues';

export type PublisherDashboardActivityRange = '7d' | '30d' | 'all';

/** One activity row on the dashboard: what happened, which release it belongs to, and how it filters. */
export interface PublisherDashboardActivityItem {
  event: PublisherReleaseEvent;
  release: PublisherRelease | null;
  type: PublisherReleaseEventType;
}

/** One chronological section in the dashboard feed, e.g. Today or Last Week. */
export interface PublisherDashboardActivityGroup {
  label: string;
  items: PublisherDashboardActivityItem[];
}

/** Everything the dashboard route needs, already grouped and counted. */
export interface PublisherDashboardData {
  overview: {
    publishedSongs: number;
    liveReleases: number;
    inProgressReleases: number;
    needsAttention: number;
    drafts: number;
    deliveredOutlets: number;
  };
  allReleases: PublisherRelease[];
  liveReleases: PublisherRelease[];
  resumeReleases: PublisherRelease[];
  needsAttentionReleases: PublisherRelease[];
  recentActivity: PublisherDashboardActivityItem[];
  activity: PublisherDashboardActivityItem[];
  releaseTypes: PublisherDashboardBucket[];
  genres: PublisherDashboardBucket[];
  languages: PublisherDashboardBucket[];
  explicitness: PublisherDashboardBucket[];
}

const SEED_SONG_REF_RE = /^__seed_song_(\d+)__$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ACTIVITY_FILTER_TYPES: Readonly<Record<Exclude<PublisherDashboardActivityFilter, 'all'>, PublisherReleaseEventType>> = {
  milestones: 'milestone',
  platform_updates: 'platform_update',
  earnings: 'earning',
  issues: 'issue',
};

function sortBySeqDesc(releases: readonly PublisherRelease[]): PublisherRelease[] {
  return [...releases].sort((a, b) => b.seq - a.seq);
}

function sortByTimestampDesc<T extends { at: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => b.at.localeCompare(a.at));
}

function bucket(
  values: readonly string[],
  emptyLabel: string,
  format?: (value: string) => string
): PublisherDashboardBucket[] {
  const counts = new Map<string, number>();
  values.forEach((value) => {
    const key = value.trim() || emptyLabel;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return [...counts.entries()]
    .map(([label, count]) => ({ label: format ? format(label) : label, count }))
    .sort((a, b) => (b.count === a.count ? a.label.localeCompare(b.label) : b.count - a.count));
}

function resolveSeedSongRef(value: string | null | undefined, songs: readonly Song[]): string | null | undefined {
  if (!value) return value;
  const match = SEED_SONG_REF_RE.exec(value);
  if (!match) return value;
  const index = Number(match[1]);
  return songs[index]?.id ?? value;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseEventDate(event: PublisherReleaseEvent): Date | null {
  const parsed = new Date(event.at);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function relativeDayDifference(now: Date, value: Date): number {
  return Math.floor((startOfLocalDay(now).getTime() - startOfLocalDay(value).getTime()) / MS_PER_DAY);
}

function olderActivityGroupLabel(date: Date): string {
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function buildActivityItems(
  releases: readonly PublisherRelease[],
  events: readonly PublisherReleaseEvent[]
): PublisherDashboardActivityItem[] {
  const releaseById = new Map(releases.map((release) => [release.id, release]));
  return sortByTimestampDesc(events).map((event) => ({
    event,
    release: releaseById.get(event.releaseId) ?? null,
    type: resolvePublisherActivityType(event),
  }));
}

/**
 * Rewrites the JSON fixture's seed-song placeholders to the ids the mock songs table really owns.
 *
 * The seed songs are created once in api/mockCatalogue.ts and copied outward from there, which
 * means their ids are stable within a process but not knowable in a committed JSON file. The
 * placeholders let the fixture stay readable on disk while the dashboard still links back to the
 * real seeded library at runtime.
 */
export function materializePublisherMockSnapshot(
  template: PublisherStateSnapshot,
  songs: readonly Song[]
): PublisherStateSnapshot {
  return {
    releases: template.releases.map((release) => ({
      ...release,
      songIds: release.songIds.map((songId) => resolveSeedSongRef(songId, songs) ?? songId),
      artworkFromSongId: resolveSeedSongRef(release.artworkFromSongId ?? null, songs) ?? release.artworkFromSongId,
    })),
    payments: template.payments.map((payment) => ({ ...payment })),
    events: template.events.map((event) => ({ ...event })),
    platforms: template.platforms.map((platform) => ({ ...platform })),
  };
}

/**
 * A truthful one-line summary of provider-reported platform rows.
 *
 * The wording is deliberately about delivery and reporting, never about a release being live in a
 * store. `describeQueueStatus()` already encodes the provider vocabulary boundary, so this helper
 * counts only what that function classifies as delivered.
 */
export function deliverySummaryLabel(platforms: readonly PublisherReleasePlatform[]): string | null {
  if (platforms.length === 0) return null;
  const delivered = platforms.filter((platform) => describeQueueStatus(platform.status) === 'delivered').length;
  if (delivered > 0) {
    return delivered === 1 ? '1 outlet delivered' : `${delivered} outlets delivered`;
  }
  return platforms.length === 1 ? '1 outlet update reported' : `${platforms.length} outlet updates reported`;
}

/**
 * Best-effort category for legacy activity rows that were stored before explicit event types.
 *
 * Message text is a fallback only: new rows should set `event.type` directly, because copy is a
 * presentation surface and filters need stable semantics.
 */
export function resolvePublisherActivityType(event: PublisherReleaseEvent): PublisherReleaseEventType {
  if (event.type) return event.type;
  const text = `${event.message} ${event.detail ?? ''}`.trim().toLowerCase();

  if (/earnings?|gross|revenue|royalt|payout|reported amount|usd|\$/.test(text)) return 'earning';
  if (/refused|rejected|failed|could not|action needed|needs attention|mismatch|missing|error|issue/.test(text)) {
    return 'issue';
  }
  if (/distributed|distribution|platform|outlet|spotify|apple music|tidal|tiktok|pandora|processing|submitted/.test(text)) {
    return 'platform_update';
  }
  if (/milestone|stream|playlist|approved|draft created|uploaded|first /.test(text)) return 'milestone';
  return event.source === 'provider' ? 'platform_update' : 'milestone';
}

/** Filters the dashboard feed by the selected event category. */
export function filterPublisherActivityByType(
  items: readonly PublisherDashboardActivityItem[],
  filter: PublisherDashboardActivityFilter
): PublisherDashboardActivityItem[] {
  if (filter === 'all') return [...items];
  const wanted = ACTIVITY_FILTER_TYPES[filter];
  return items.filter((item) => item.type === wanted);
}

/** Filters the dashboard feed to a relative time window in local time. */
export function filterPublisherActivityByRange(
  items: readonly PublisherDashboardActivityItem[],
  range: PublisherDashboardActivityRange,
  nowInput: Date = new Date()
): PublisherDashboardActivityItem[] {
  if (range === 'all') return [...items];
  const windowDays = range === '7d' ? 7 : 30;
  const now = new Date(nowInput);
  const threshold = startOfLocalDay(now).getTime() - (windowDays - 1) * MS_PER_DAY;
  return items.filter((item) => {
    const date = parseEventDate(item.event);
    return date ? date.getTime() >= threshold : false;
  });
}

/** Groups already-filtered activity into the date buckets the dashboard prints. */
export function groupPublisherActivity(
  items: readonly PublisherDashboardActivityItem[],
  nowInput: Date = new Date()
): PublisherDashboardActivityGroup[] {
  const now = new Date(nowInput);
  const groups: PublisherDashboardActivityGroup[] = [];

  items.forEach((item) => {
    const date = parseEventDate(item.event);
    const label = (() => {
      if (!date) return NO_DATE;
      const ageInDays = relativeDayDifference(now, date);
      if (ageInDays <= 0) return 'Today';
      if (ageInDays === 1) return 'Yesterday';
      if (ageInDays < 7) return 'Last Week';
      return olderActivityGroupLabel(date);
    })();

    const existing = groups[groups.length - 1];
    if (existing?.label === label) {
      existing.items.push(item);
      return;
    }
    groups.push({ label, items: [item] });
  });

  return groups;
}

/** Builds the grouped sections and overview tiles for the Publisher dashboard. */
export function buildPublisherDashboard(input: {
  releases: readonly PublisherRelease[];
  events: readonly PublisherReleaseEvent[];
  platforms: readonly PublisherReleasePlatform[];
}): PublisherDashboardData {
  const releases = sortBySeqDesc(input.releases);
  const liveReleases = releases.filter((release) => release.status === 'live');
  const resumeReleases = releases.filter((release) =>
    release.status === 'draft' || release.status === 'in_review' || release.status === 'processing'
  );
  const needsAttentionReleases = releases.filter((release) => release.status === 'failed');
  const activity = buildActivityItems(releases, input.events);

  const publishedSongs = releases.length;
  const deliveredOutlets = input.platforms.filter((platform) => describeQueueStatus(platform.status) === 'delivered').length;
  const recentActivity = activity.slice(0, 6);

  /*
    The snapshot panel is about what is already in the catalogue if any releases are live; when
    nothing has been delivered yet, falling back to all releases still gives the dashboard a useful
    summary instead of a blank card group.
  */
  const snapshotSource = liveReleases.length > 0 ? liveReleases : releases;

  return {
    overview: {
      publishedSongs,
      liveReleases: liveReleases.length,
      inProgressReleases: releases.filter(
        (release) => release.status === 'in_review' || release.status === 'processing'
      ).length,
      needsAttention: needsAttentionReleases.length,
      drafts: releases.filter((release) => release.status === 'draft').length,
      deliveredOutlets,
    },
    allReleases: releases,
    liveReleases,
    resumeReleases,
    needsAttentionReleases,
    recentActivity,
    activity,
    releaseTypes: bucket(snapshotSource.map((release) => release.releaseType), 'Unspecified'),
    genres: bucket(snapshotSource.map((release) => release.genre), 'Genre not set'),
    languages: bucket(snapshotSource.map((release) => release.language), 'Language not set', (value) =>
      value === 'Language not set' ? value : languageLabel(value)
    ),
    explicitness: [
      {
        label: 'Clean',
        count: snapshotSource.filter((release) => !release.explicit).length,
      },
      {
        label: 'Explicit',
        count: snapshotSource.filter((release) => release.explicit).length,
      },
    ],
  };
}
